import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  FaCar,
  FaHotel,
  FaUserTie,
  FaStar,
  FaSearch,
  FaFilter,
  FaChevronLeft,
  FaChevronRight,
  FaUsers,
  FaTasks,
  FaCampground,
  FaHiking,
} from 'react-icons/fa'
import serviceService from '../services/serviceService'
import { toast, ToastContainer } from 'react-toastify'
import Loader from '../components/Loader'
import 'react-toastify/dist/ReactToastify.css'
import { getServiceImage } from '../utils/helpers'
import SEO from '../utils/SEO'
import Button from '../components/ui/Button'

const Services = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('all')
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
  })
  const [filters, setFilters] = useState({
    search: '',
    minPrice: '',
    maxPrice: '',
    minCapacity: '',
  })
  const [showFilters, setShowFilters] = useState(false)

  // Set active tab from URL parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam && ['all', 'car', 'hotel', 'guide', 'activity', 'camp_site'].includes(tabParam)) {
      setActiveTab(tabParam)
    }
  }, [searchParams])

  useEffect(() => {
    loadServices()
  }, [activeTab])

  const loadServices = async (filterParams = {}, page = 1) => {
    try {
      setLoading(true)
      const params = {
        ...filterParams,
        type: activeTab === 'all' ? undefined : activeTab,
        page,
        limit: 20,
      }
      const data = await serviceService.getAllServices(params)
      setServices(data.services || [])
      setPagination({
        page: data.page || 1,
        totalPages: data.totalPages || 1,
        total: data.count || data.total || 0,
      })

      // Scroll to top of services
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (error) {
      toast.error('Error loading services')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    })
  }

  const applyFilters = () => {
    const activeFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== ''),
    )
    loadServices(activeFilters, 1) // Reset to page 1 on new search
  }

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return
    const activeFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== ''),
    )
    loadServices(activeFilters, newPage)
  }

  const clearFilters = () => {
    setFilters({
      search: '',
      minPrice: '',
      maxPrice: '',
      minCapacity: '',
    })
    loadServices({}, 1)
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      applyFilters()
    }
  }

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    setFilters({
      search: '',
      minPrice: '',
      maxPrice: '',
      minCapacity: '',
    })
  }

  const tabs = [
    { id: 'all', label: 'All Services', icon: FaStar },
    { id: 'car', label: 'Cars', icon: FaCar },
    { id: 'hotel', label: 'Hotels', icon: FaHotel },
    { id: 'guide', label: 'Guides', icon: FaUserTie },
    { id: 'camp_site', label: 'Camp Sites', icon: FaCampground },
    { id: 'activity', label: 'Adventures', icon: FaHiking },
  ]

  const getServiceTypeLabel = (type) => {
    const labels = {
      car: 'Car Rental',
      hotel: 'Hotel',
      guide: 'Tour Guide',
      camp_site: 'Camp Site',
      activity: 'Activity',
    }
    return labels[type] || type
  }

  const handleBookService = (service) => {
    // Navigate directly to standalone service booking page
    navigate(`/services/${service.id}/book`)
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEO
        title="Travel Services - Hotels, Cars, Guides & Camp Sites"
        description="Book premium travel services in Himachal Pradesh. Hotels, car rentals, professional tour guides, camp sites, and adventure activities across Shimla, Manali, Dharamshala."
        canonical="/services"
        keywords="hotels Shimla, car rental Manali, tour guide Himachal Pradesh, camping Himachal, travel services India"
      />
      <ToastContainer />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-display font-bold text-gray-900 mb-4">Our Services</h1>
          <p className="text-gray-600 text-lg">
            Choose from our premium selection of cars, hotels, camp sites, guides, and adventures
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'toggle-active' : 'toggle-inactive'}
                size="md"
                icon={<Icon />}
                onClick={() => handleTabChange(tab.id)}
              >
                {tab.label}
              </Button>
            )
          })}
        </div>

        {/* Search & Filter Bar */}
        <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  onKeyPress={handleKeyPress}
                  placeholder={`Search ${activeTab === 'all' ? 'services' : { car: 'cars', hotel: 'hotels', guide: 'guides', camp_site: 'camp sites', activity: 'adventures' }[activeTab] || 'services'}...`}
                  className="input-field !pl-10"
                />
              </div>
            </div>

            {/* Filter Toggle */}
            <Button
              variant="secondary"
              icon={<FaFilter />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filters
            </Button>

            {/* Search Button */}
            <Button onClick={applyFilters}>Search</Button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-white/30">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="label">Min Price (₹)</label>
                  <input
                    type="number"
                    name="minPrice"
                    value={filters.minPrice}
                    onChange={handleFilterChange}
                    onKeyPress={handleKeyPress}
                    placeholder="0"
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="label">Max Price (₹)</label>
                  <input
                    type="number"
                    name="maxPrice"
                    value={filters.maxPrice}
                    onChange={handleFilterChange}
                    onKeyPress={handleKeyPress}
                    placeholder="100000"
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="label">Min Capacity</label>
                  <input
                    type="number"
                    name="minCapacity"
                    value={filters.minCapacity}
                    onChange={handleFilterChange}
                    onKeyPress={handleKeyPress}
                    placeholder="1"
                    className="input-field"
                  />
                </div>

                <div className="flex items-end md:col-span-3">
                  <Button variant="ghost" fullWidth onClick={clearFilters}>
                    Clear Filters
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Services Grid */}
        {loading ? (
          <Loader />
        ) : services.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No services found. Try adjusting your filters.</p>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <p className="text-gray-600">
                Showing <span className="font-semibold">{services.length}</span> of{' '}
                <span className="font-semibold">{pagination.total}</span> services
                {pagination.totalPages > 1 && (
                  <span className="ml-2">
                    (Page {pagination.page} of {pagination.totalPages})
                  </span>
                )}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service) => (
                <div key={service.id} className="card group hover:shadow-xl transition-shadow">
                  <div className="relative h-64 overflow-hidden shine-on-hover">
                    <img
                      src={getServiceImage(service)}
                      alt={service.name}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-xs font-semibold uppercase">
                        {getServiceTypeLabel(service.type)}
                      </span>
                    </div>
                    <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full text-sm font-bold text-primary-600">
                      ₹{service.base_price.toLocaleString()}/{service.price_unit.replace('_', ' ')}
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
                      <FaUsers />
                      <span>Capacity: {service.capacity}</span>
                      {service.is_active ? (
                        <>
                          <span>•</span>
                          <span className="text-green-600 font-semibold">Available</span>
                        </>
                      ) : (
                        <>
                          <span>•</span>
                          <span className="text-red-600 font-semibold">Unavailable</span>
                        </>
                      )}
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition">
                      {service.name}
                    </h3>

                    <p className="text-gray-600 mb-4 line-clamp-2">{service.description}</p>

                    {/* Amenities */}
                    {service.amenities && service.amenities.length > 0 && (
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-2">
                          {service.amenities.slice(0, 3).map((amenity, idx) => (
                            <span
                              key={idx}
                              className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                            >
                              {amenity}
                            </span>
                          ))}
                          {service.amenities.length > 3 && (
                            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                              +{service.amenities.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-1">
                        <FaStar className="text-yellow-400" />
                        <span className="font-semibold">{service.avg_rating || 0}</span>
                        {service.review_count > 0 ? (
                          <span className="text-gray-500 text-sm">({service.review_count})</span>
                        ) : (
                          <span className="bg-primary-100 text-primary-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                            New
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <Link
                          to={`/services/${service.id}`}
                          className="text-gray-700 font-semibold hover:underline transition"
                        >
                          See Details
                        </Link>
                        <button
                          onClick={() => handleBookService(service)}
                          className="link-primary font-semibold"
                        >
                          Reserve Now →
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 mt-12">
                <Button
                  variant="pagination"
                  size="sm"
                  icon={<FaChevronLeft />}
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1 || loading}
                >
                  Previous
                </Button>

                <div className="flex space-x-2">
                  {[...Array(pagination.totalPages)].map((_, idx) => {
                    const pageNum = idx + 1
                    // Show first page, last page, current page, and pages around current
                    if (
                      pageNum === 1 ||
                      pageNum === pagination.totalPages ||
                      (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1)
                    ) {
                      return (
                        <Button
                          key={pageNum}
                          variant={pageNum === pagination.page ? 'pagination-active' : 'pagination'}
                          size="sm"
                          onClick={() => handlePageChange(pageNum)}
                          disabled={loading}
                        >
                          {pageNum}
                        </Button>
                      )
                    } else if (pageNum === pagination.page - 2 || pageNum === pagination.page + 2) {
                      return (
                        <span key={pageNum} className="px-2 py-2">
                          ...
                        </span>
                      )
                    }
                    return null
                  })}
                </div>

                <Button
                  variant="pagination"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages || loading}
                >
                  Next
                  <FaChevronRight className="ml-2" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default Services
